import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2'; // Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // Import the CSS for styling
import './Sessions.css';
import { FiMail, FiPhone, FiCalendar, FiClock, FiVideo, FiFileText, FiGlobe, FiXCircle } from 'react-icons/fi';
import EmailModal from './EmailModal';

const API_URL = process.env.REACT_APP_API_URL;

// --- RescheduleModal Component ---
const RescheduleModal = ({ session, onClose, onSave }) => {
    const initialDateTime = new Date(session.dateTime);
    const [newDate, setNewDate] = useState(initialDateTime.toISOString().split('T')[0]);
    const [newTime, setNewTime] = useState(
        initialDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()
    );
    const [newDuration, setNewDuration] = useState(session.duration?.toString() || '20');

    const handleSave = () => {
        if (!newDate || !newTime) {
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'warning',
                title: 'Incomplete Information',
                text: 'Please select a new date and time for the session.',
            });
            return;
        }
        onSave(session._id, { date: newDate, time: newTime, duration: newDuration });
    };
    
    // ... rest of RescheduleModal is unchanged
    const availableTimes = [
        '10:30 am', '10:40 am', '10:50 am', '11:00 am', '11:10 am', '11:20 am', '11:30 am', 
        '11:40 am', '11:50 am', '12:00 pm', '12:10 pm', '12:20 pm', '12:30 pm', '12:40 pm', 
        '12:50 pm', '1:00 pm', '1:10 pm', '1:20 pm'
    ];
    
    const formattedDateForLabel = new Date(newDate + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    const currentSlotFormatted = new Date(session.dateTime).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });

    return (
        <div className="modal__overlay" onClick={onClose}>
            <div className="modal__content reschedule-modal" onClick={e => e.stopPropagation()}>
                <button className="modal__close-btn" onClick={onClose}>×</button>
                <div className="modal__grid">
                    <div className="modal__panel modal__panel--info">
                        <div className="modal__panel-content">
                            <h2>Reschedule Session</h2>
                            <div className="modal__reschedule-details">
                                <h4>Student:</h4>
                                <p>{session.studentName}</p>
                                <h4>Current Slot:</h4>
                                <p>{currentSlotFormatted}</p>
                            </div>
                        </div>
                        <button onClick={handleSave} className="modal__confirm-btn" disabled={!newTime}>
                            Confirm New Schedule
                        </button>
                    </div>
                    <div className="modal__panel modal__panel--form">
                        <div className="modal__header"><h2>Select New Appointment</h2></div>
                        <p className="modal__subtitle">Select a new date and time for the session.</p>
                        
                        <div className="modal__form-group">
                            <label htmlFor="date">New Date</label>
                            <input type="date" id="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                        </div>

                        <div className="modal__form-group">
                            <label>Duration</label>
                            <div className="modal__duration-options">
                                {['10', '20', '30'].map(d => (
                                    <label key={d}>
                                        <input type="radio" name="duration" value={d} checked={newDuration === d} onChange={(e) => setNewDuration(e.target.value)} />
                                        <span>{d} minutes</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        
                        <div className="modal__form-group">
                            <label>Available Times for {formattedDateForLabel}</label>
                            <div className="modal__time-slots">
                                {availableTimes.map(time => (
                                    <button 
                                        key={time} 
                                        className={`modal__time-slot-btn ${newTime === time ? 'modal__time-slot-btn--selected' : ''}`} 
                                        onClick={() => setNewTime(time)}
                                    >
                                        {time}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


// --- Main Sessions Component ---
const Sessions = () => {
    // ... states are unchanged
    const [sessions, setSessions] = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([]); 
    const [filters, setFilters] = useState({ name: '', date: '', phone: '', status: '' });
    const [activeTab, setActiveTab] = useState('upcoming');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sessionToReschedule, setSessionToReschedule] = useState(null);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [sessionForEmail, setSessionForEmail] = useState(null);

    const openEmailModal = (session) => {
        setSessionForEmail(session);
        setIsEmailModalOpen(true);
    };

    const closeEmailModal = () => {
        setIsEmailModalOpen(false);
        setSessionForEmail(null);
    };

    const fetchSessions = () => {
        axios.get(`${API_URL}/api/sessions`)
            .then(res => setSessions(res.data.filter(s => s.status !== 'Cancelled')))
            .catch(err => console.error("Error fetching sessions:", err));
    };

    useEffect(() => { fetchSessions(); }, []);

    // ... filtering useEffect is unchanged
    useEffect(() => {
        let result = sessions;
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        if (filters.name) result = result.filter(s => s.studentName.toLowerCase().includes(filters.name.toLowerCase()));
        if (filters.date) result = result.filter(s => s.dateTime && s.dateTime.split('T')[0] === filters.date);
        if (filters.phone) result = result.filter(s => s.phone?.includes(filters.phone));
        if (filters.status) {
            if (filters.status === 'completed') result = result.filter(s => s.dateTime && new Date(s.dateTime) < todayStart);
            else if (filters.status === 'pending') result = result.filter(s => s.dateTime && new Date(s.dateTime) >= todayStart);
        }
        setFilteredSessions(result);
    }, [filters, sessions]);
    
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
    };
    
    const clearFilters = () => {
        setFilters({ name: '', date: '', phone: '', status: '' });
    };

    const handleSendEmail = async ({ subject, body }) => {
        if (!sessionForEmail) return;
        try {
            await axios.post(`${API_URL}/api/sessions/${sessionForEmail._id}/send-email`, { subject, body });
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'success',
                title: 'Email Sent!',
                text: `The email was sent successfully to ${sessionForEmail.studentName}.`,
                timer: 2500,
                showConfirmButton: false
            });
            closeEmailModal();
        } catch (error) {
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'error',
                title: 'Send Failed',
                text: 'There was a problem sending the email. Please try again.',
            });
            console.error(error);
        }
    };

    const handleCancelSession = (sessionId) => {
        // MODIFIED: Replaced window.confirm with Swal
        Swal.fire({
            title: 'Are you sure?',
            text: "This will cancel the session and notify the user. This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, cancel it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.patch(`${API_URL}/api/sessions/${sessionId}/cancel`);
                    // Success Swal
                    Swal.fire(
                        'Cancelled!',
                        'The session has been successfully cancelled.',
                        'success'
                    );
                    fetchSessions();
                } catch (error) {
                    // Error Swal
                    Swal.fire(
                        'Failed!',
                        'The session could not be cancelled. Please try again.',
                        'error'
                    );
                    console.error(error);
                }
            }
        });
    };

    const handleSaveReschedule = async (id, newScheduleData) => {
        try {
            await axios.put(`${API_URL}/api/sessions/${id}`, newScheduleData);
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'success',
                title: 'Rescheduled!',
                text: 'The session has been updated successfully.',
                timer: 2000,
                showConfirmButton: false
            });
            setIsModalOpen(false);
            setSessionToReschedule(null);
            fetchSessions();
        } catch (err) {
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'error',
                title: 'Reschedule Failed',
                text: 'There was a problem rescheduling the session. Please try again.',
            });
            console.error(err);
        }
    };

    const openRescheduleModal = (session) => {
        setSessionToReschedule(session);
        setIsModalOpen(true);
    };

    const getSectionData = () => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const getSessionDate = (session) => session.dateTime ? new Date(session.dateTime) : null;
        if (activeTab === 'All Sessions') return filteredSessions;
        if (activeTab === 'today') return filteredSessions.filter(s => {
            const sessionDate = getSessionDate(s);
            return sessionDate && sessionDate.toDateString() === todayStart.toDateString();
        });
        if (activeTab === 'completed') return filteredSessions.filter(s => {
            const sessionDate = getSessionDate(s);
            return sessionDate && sessionDate < todayStart;
        });
        return filteredSessions.filter(s => {
            const sessionDate = getSessionDate(s);
            return sessionDate && sessionDate >= todayStart;
        });
    };

    // --- The rest of the return/JSX is correct and unchanged ---
    return (
        <div className="sessions-page">
            <h1 className="sessions-page__title">Counselling Sessions</h1>
            
            <div className="sessions-page__controls">
                <input name="name" className="sessions-page__filter-input" type="text" placeholder="Filter by name..." value={filters.name} onChange={handleFilterChange} />
                <input name="phone" className="sessions-page__filter-input" type="tel" placeholder="Filter by phone..." value={filters.phone} onChange={handleFilterChange} />
                <select name="status" className="sessions-page__filter-input" value={filters.status} onChange={handleFilterChange}>
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                </select>
                <input name="date" className="sessions-page__filter-input" type="date" value={filters.date} onChange={handleFilterChange} />
                <button className="sessions-page__clear-btn" onClick={clearFilters}>Clear</button>
            </div>

            <div className="sessions-page__tabs">
                <button className={`sessions-page__tab-btn ${activeTab === 'All Sessions' ? 'sessions-page__tab-btn--active' : ''}`} onClick={() => setActiveTab('All Sessions')}>All Sessions</button>
                <button className={`sessions-page__tab-btn ${activeTab === 'upcoming' ? 'sessions-page__tab-btn--active' : ''}`} onClick={() => setActiveTab('upcoming')}>Upcoming</button>
                <button className={`sessions-page__tab-btn ${activeTab === 'today' ? 'sessions-page__tab-btn--active' : ''}`} onClick={() => setActiveTab('today')}>Today's</button>
                <button className={`sessions-page__tab-btn ${activeTab === 'completed' ? 'sessions-page__tab-btn--active' : ''}`} onClick={() => setActiveTab('completed')}>Completed</button>
            </div>

            <div className="session-grid">
                {getSectionData().length > 0 ? getSectionData().map((s) => (
                    <div className="session-card" key={s._id}>
                        <div className="session-card__header">
                            <h3 className="session-card__student-name">{s.studentName}</h3>
                            <span className={`session-card__status session-card__status--${s.status?.toLowerCase()}`}>{s.status}</span>
                        </div>
                        <div className="session-card__details">
                            <p className="session-card__detail-item"><FiMail /> {s.studentEmail}</p>
                            <p className="session-card__detail-item"><FiPhone /> {s.phone}</p>
                            <p className="session-card__detail-item"><FiCalendar /> {new Date(s.dateTime).toLocaleDateString('en-GB')}</p>
                            <p className="session-card__detail-item"><FiClock /> {new Date(s.dateTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                            <p className="session-card__detail-item"><FiVideo /> {s.mode}</p>
                            <p className="session-card__detail-item"><FiFileText /> {s.subject}</p>
                            {s.countries?.map((country, index) => (<p key={index} className="session-card__detail-item"><FiGlobe /> {country}</p>))}
                        </div>
                        <div className="session-card__actions">
                            {s.mode === 'Video' && (s.zoomStartUrl || s.zoomMeetingLink) ? (
                                <button className="session-card__btn session-card__btn--primary" onClick={() => window.open(s.zoomStartUrl || s.zoomMeetingLink, '_blank')}>Join Session</button>
                            ) : s.mode === 'Video' ? (
                                <button className="session-card__btn session-card__btn--disabled" disabled>No Link</button>
                            ) : (
                                <button className="session-card__btn session-card__btn--disabled" disabled>Phone Call</button>
                            )}
                            <button className="session-card__btn session-card__btn--secondary" onClick={() => openEmailModal(s)}>Email</button>
                            <button className="session-card__btn session-card__btn--secondary" onClick={() => openRescheduleModal(s)}>Reschedule</button>
                            <button className="session-card__btn session-card__btn--danger" onClick={() => handleCancelSession(s._id)}><FiXCircle /> Cancel</button>
                        </div>
                    </div>
                )) : (
                    <div className="session-grid__no-results">
                        <p>No sessions match the current filters.</p>
                    </div>
                )}
            </div>

            {isModalOpen && sessionToReschedule && (
                <RescheduleModal session={sessionToReschedule} onClose={() => setIsModalOpen(false)} onSave={handleSaveReschedule} />
            )}

            {isEmailModalOpen && sessionForEmail && (
                <EmailModal session={sessionForEmail} onClose={closeEmailModal} onSend={handleSendEmail} />
            )}
        </div>
    );
};

export default Sessions;